import { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Card, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import styles from './OctadeskRegras.module.css';

/**
 * Palavras de Reclamação — única tela de regras do Octadesk (2026-09-16).
 *
 * Esta página TINHA 4 abas (Horário de Funcionamento, Feriados, Informações da Loja
 * e FAQs) que alimentavam o prompt da IA. Foram removidas a pedido do dono: nunca
 * entraram em uso — em produção havia 4 registros, todos INATIVOS desde 04/06/2026,
 * e nenhum FAQ ou feriado jamais foi cadastrado. Como a query do backend filtrava
 * `ativo = 1`, a IA já recebia contexto vazio; remover não mudou o comportamento.
 *
 * O que sobra aqui é diferente em natureza: estas frases não ajudam a IA a responder
 * — elas CLASSIFICAM a conversa sem chamar a IA (camada 1d da triagem).
 */

// Espelha SUBCATEGORIAS_RECLAMACAO_ACEITAS no backend (lista fechada).
const SUBCATEGORIAS_RECLAMACAO = [
  { key: 'defeito',           label: '🔧 Defeito na peça' },
  { key: 'produto_errado',    label: '📦 Produto ou item errado' },
  { key: 'atraso_entrega',    label: '🚚 Atraso na entrega' },
  { key: 'mau_atendimento',   label: '🙍 Mau atendimento' },
  { key: 'qualidade',         label: '⭐ Qualidade' },
  { key: 'cobranca_indevida', label: '💳 Cobrança indevida' },
  { key: 'site_erro',         label: '🐞 Erro no site' },
  { key: 'site_indisponivel', label: '🌐 Site fora do ar' },
  { key: 'login_cadastro',    label: '🔑 Login / cadastro' },
  { key: 'falha_pagamento',   label: '💰 Falha no pagamento' },
];
// O que a palavra FAZ quando aparece na conversa. Não há mais limite de tamanho:
// qualidade não tem relação com número de letras (medido em 90 dias, "quebrou" erra
// 0% e "atraso" erra 100%), então quem decide o peso é o dono, não uma régua.
const EFEITOS = [
  { key: 'classificar', label: 'Classificar como reclamação',
    ajuda: 'A conversa vira reclamação com urgência alta na hora, sem gastar IA. Use com frases específicas.' },
  { key: 'alertar', label: 'Só alertar (⚠️)',
    ajuda: 'Não muda a classificação: a IA decide normalmente e a conversa fica marcada pro atendente olhar. Mais seguro para palavras soltas.' },
];
// Sugestão (não trava): palavra única costuma ser ambígua → nasce como alerta.
const efeitoSugerido = (frase) => (/\s/.test(frase.trim()) ? 'classificar' : 'alertar');

export default function OctadeskRegras() {
  const { isAdmin } = useAuth();
  const [grupos, setGrupos] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get('/api/octadesk/regras');
      if (r.data?.success) setGrupos(r.data.data || {});
    } catch (e) {
      setMensagem({ tipo: 'danger', texto: e.message });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (id, dados) => {
    try {
      await api.put(`/api/octadesk/regras/${id}`, dados);
      setMensagem({ tipo: 'success', texto: '✅ Salvo' });
      await carregar();
      setTimeout(() => setMensagem(null), 2000);
    } catch (e) {
      setMensagem({ tipo: 'danger', texto: '❌ ' + (e.response?.data?.message || e.message) });
    }
  };

  const criar = async (tipo, payload) => {
    try {
      await api.post('/api/octadesk/regras', { tipo, ...payload });
      await carregar();
      setMensagem({ tipo: 'success', texto: '✅ Criado' });
      setTimeout(() => setMensagem(null), 2000);
    } catch (e) {
      setMensagem({ tipo: 'danger', texto: '❌ ' + (e.response?.data?.message || e.message) });
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta palavra-chave?')) return;
    try {
      await api.delete(`/api/octadesk/regras/${id}`);
      await carregar();
      setMensagem({ tipo: 'success', texto: '✅ Removido' });
      setTimeout(() => setMensagem(null), 2000);
    } catch (e) {
      setMensagem({ tipo: 'danger', texto: '❌ ' + (e.response?.data?.message || e.message) });
    }
  };

  if (carregando) return (
    <Container className="py-5 text-center"><Spinner animation="border" variant="primary" /></Container>
  );

  return (
    <Container fluid className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>🔴</div>
        <div className={styles.headerText}>
          <h2>Palavras de Reclamação</h2>
          <p className={styles.subtitle}>
            Quando uma destas frases aparece na mensagem do cliente, a conversa é
            marcada como <strong>reclamação com urgência alta</strong> na hora, sem
            gastar IA. O sistema já vem com uma lista embutida — o que você cadastrar
            aqui soma a ela.
          </p>
        </div>
      </div>

      {mensagem && <Alert variant={mensagem.tipo} className="mt-2">{mensagem.texto}</Alert>}

      {isAdmin?.() ? (
        <PalavrasReclamacaoPanel
          regras={grupos.regra_subcategoria_reclamacao || []}
          salvar={salvar} criar={criar} remover={remover}
        />
      ) : (
        <Alert variant="secondary" className="mt-3">
          Só administradores podem alterar estas palavras — elas classificam conversas
          sozinhas, sem a IA revisar.
        </Alert>
      )}
    </Container>
  );
}

function PalavrasReclamacaoPanel({ regras, salvar, criar, remover }) {
  const [novo, setNovo] = useState({ sub: 'defeito', frase: '', efeito: null });
  const frase = novo.frase.trim();
  const valida = frase.length > 0; // única exigência — sem limite de tamanho
  // Enquanto o dono não escolher, segue a sugestão que muda conforme ele digita.
  const efeito = novo.efeito || efeitoSugerido(novo.frase);

  const rotulo = (k) => SUBCATEGORIAS_RECLAMACAO.find(s => s.key === k)?.label || k;

  return (
    <div className="mt-3">
      <Card className={styles.card}>
        <Card.Body>
          <h6>Adicionar palavra-chave</h6>
          <p className={styles.subtitle}>
            Escreva como o cliente escreve (ex.: <em>chegou com avaria</em>,
            <em> veio errado</em>, <em>quebrou</em>). Qualquer tamanho serve — o que
            importa é escolher abaixo <strong>o que a palavra faz</strong>. Dica:
            palavra solta como <em>problema</em> ou <em>ruim</em> aparece bastante em
            conversa que não é reclamação, então costuma render mais como alerta.
          </p>
          <Form.Select
            value={novo.sub}
            onChange={(e) => setNovo({ ...novo, sub: e.target.value })}
            className="mb-2"
          >
            {SUBCATEGORIAS_RECLAMACAO.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </Form.Select>
          <Form.Control
            placeholder='Frase do cliente (ex.: "chegou com avaria")'
            value={novo.frase}
            onChange={(e) => setNovo({ ...novo, frase: e.target.value })}
            className="mb-2"
          />
          <div className="mb-2">
            {EFEITOS.map(e => (
              <Form.Check
                key={e.key}
                type="radio"
                name="efeito"
                id={`efeito-${e.key}`}
                checked={efeito === e.key}
                onChange={() => setNovo({ ...novo, efeito: e.key })}
                label={<><strong>{e.label}</strong> <small className="text-muted">— {e.ajuda}</small></>}
              />
            ))}
          </div>
          <Button
            onClick={() => {
              if (!valida) return;
              criar('regra_subcategoria_reclamacao', {
                chave: novo.sub,
                titulo: frase,
                valor: { palavras: [frase], efeito },
                ativo: true,
              });
              setNovo({ sub: novo.sub, frase: '', efeito: null });
            }}
            disabled={!valida}
          >
            + Adicionar
          </Button>
        </Card.Body>
      </Card>

      {regras.length === 0 ? (
        <p className={styles.empty}>
          Nenhuma palavra cadastrada. A lista embutida (defeito, avaria, produto
          errado, atraso, cobrança…) já está valendo — cadastre aqui só o que faltar.
        </p>
      ) : (
        <div className={styles.lista}>
          {regras.map(r => (
            <Card key={r.id} className={styles.itemCard}>
              <Card.Body>
                <div className="mb-2">
                  <Badge bg="danger" className="me-2">{rotulo(r.chave)}</Badge>
                  <Badge bg={(r.valor?.efeito || 'classificar') === 'alertar' ? 'secondary' : 'dark'} className="me-2">
                    {(r.valor?.efeito || 'classificar') === 'alertar' ? '⚠️ só alerta' : 'classifica'}
                  </Badge>
                  <strong>{(r.valor?.palavras || [r.titulo]).join(' · ')}</strong>
                </div>
                <div className={styles.acoes}>
                  <Form.Check
                    type="switch"
                    label="Ativa"
                    checked={r.ativo}
                    onChange={(e) => salvar(r.id, { ativo: e.target.checked })}
                  />
                  <Button variant="outline-danger" size="sm" onClick={() => remover(r.id)}>🗑️</Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
